/**
 * Compact top bar for off-canvas monograph demo navigation.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { MonographDemoTheme } from "./types.js";

export interface MonographTopBarProps {
  readonly activeLabel: string;
  readonly appLabel: string;
  readonly drawerOpen: boolean;
  readonly onToggleDrawer: () => void;
  readonly theme: MonographDemoTheme;
}

export const MONOGRAPH_TOP_BAR_HEIGHT = "3.85rem";

export function MonographTopBar({
  activeLabel,
  appLabel,
  drawerOpen,
  onToggleDrawer,
  theme,
}: MonographTopBarProps): React.JSX.Element {
  return (
    <div
      role="banner"
      className="sticky top-0 z-30 flex h-[var(--csk-monograph-top-bar-height,3.85rem)] items-center gap-3 border-b px-4 py-3 backdrop-blur-xl"
      style={{
        background: `linear-gradient(135deg, color-mix(in srgb, ${theme.surface} 88%, #f8fafc 12%), color-mix(in srgb, ${theme.void} 92%, #14b8a6 8%))`,
        borderBottom: `1px solid ${theme.border}`,
      }}
    >
      <button
        type="button"
        aria-label={drawerOpen ? "Close navigation" : "Open navigation"}
        aria-expanded={drawerOpen}
        onClick={onToggleDrawer}
        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border p-0 text-lg"
        style={{
          border: `1px solid ${theme.border}`,
          background: "rgba(248,250,252,0.07)",
          color: theme.bright,
        }}
      >
        <span aria-hidden>{drawerOpen ? "×" : "≡"}</span>
      </button>
      <div className="flex min-w-0 flex-col">
        <span
          className="font-mono text-[11px]"
          style={{
            color: theme.muted,
            letterSpacing: "0.05em",
          }}
        >
          {appLabel}
        </span>
        <span
          className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold"
          style={{
            color: theme.bright,
          }}
        >
          {activeLabel}
        </span>
      </div>
    </div>
  );
}
